import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { ClosureCreateConfirmModal } from '@/components/venue/ClosureCreateConfirmModal'
import { ClosureDeleteConfirmModal } from '@/components/venue/ClosureDeleteConfirmModal'
import { ClosureFormModal } from '@/components/venue/ClosureFormModal'
import { ClosureListTable } from '@/components/venue/ClosureListTable'
import { useCourts } from '@/hooks/useCourts'
import { useVenueClosures } from '@/hooks/useVenueClosures'
import { cn } from '@/lib/utils/cn'
import { CLOSURE_TYPE_CATALOG, type VenueClosureCreateInput, type VenueClosureItemView } from '@/lib/venue/closureTypes'
import { EMPTY_CLOSURE_FILTER, filterClosureItems, type ClosureFilter } from '@/lib/venue/closureViewModel'
import { useVenueContext } from '@/routes/venueContext'

type ModalState =
  | { type: 'create' }
  | { type: 'confirm'; input: Omit<VenueClosureCreateInput, 'force'> }
  | { type: 'delete'; item: VenueClosureItemView }
  | null

/**
 * 휴무·차단·레슨 관리 (요구사항, 설계 §2). `closures` 의 플레이스홀더를 대체한다.
 *
 * 🚨 목록 조회는 무필터 단일 쿼리다 — 유형 탭·코트 필터는 이 화면이 클라이언트에서 적용한다
 * (설계 §2 자율 결정 A1). 요약은 항상 **필터 이전** 전건 기준(서버 계약)이라 필터를 바꿔도 흔들리지 않는다.
 * 🚨 저장 가능 여부(`editable`)는 이 화면의 GET 응답 필드를 그대로 바인딩한다 — `VenueCourtsPage.tsx`
 * 와 같은 원칙.
 */
export function VenueClosuresPage() {
  const { venueId } = useVenueContext()
  const closuresQuery = useVenueClosures(venueId)
  const courtsQuery = useCourts(venueId)

  const [filter, setFilter] = useState<ClosureFilter>(EMPTY_CLOSURE_FILTER)
  const [modal, setModal] = useState<ModalState>(null)

  const view = closuresQuery.data
  const courts = courtsQuery.data?.courts.map((court) => ({ courtId: court.courtId, name: court.name })) ?? []
  const filteredItems = view ? filterClosureItems(view.items, filter) : []

  return (
    <>
      <PageHeader
        title="휴무일·차단시간 관리"
        description="휴무·차단·레슨으로 등록된 시간은 사용자에게 예약 불가로 표시되며 그 시간에는 예약이 접수되지 않습니다."
        action={
          <Button
            disabled={!view || !view.editable}
            onClick={() => {
              setModal({ type: 'create' })
            }}
          >
            휴무·차단·레슨 추가
          </Button>
        }
      />

      {closuresQuery.isLoading ? <LoadingState rows={3} label="일정을 불러오는 중" /> : null}

      {closuresQuery.isError ? (
        <ErrorState
          error={closuresQuery.error}
          onRetry={() => {
            void closuresQuery.refetch()
          }}
        />
      ) : null}

      {view ? (
        <div className="space-y-4">
          {!view.editable ? (
            <Alert tone="warning">
              <p>{view.editBlockNotice}</p>
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card>
              <CardBody>
                <p className="text-sm text-slateish-600">이번 달 휴무일 수</p>
                <p className="mt-1 text-2xl font-semibold text-ink-700">{view.summary.holidayDayCount}일</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-sm text-slateish-600">이번 달 차단 블록 수</p>
                <p className="mt-1 text-2xl font-semibold text-ink-700">{view.summary.blockCount}건</p>
              </CardBody>
            </Card>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" aria-label="유형 필터" className="flex flex-wrap gap-2">
              <button
                type="button"
                role="tab"
                aria-selected={filter.type === 'ALL'}
                onClick={() => {
                  setFilter((current) => ({ ...current, type: 'ALL' }))
                }}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  filter.type === 'ALL'
                    ? 'border-brand-500 bg-brand-50 text-brand-500'
                    : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                )}
              >
                전체
              </button>
              {CLOSURE_TYPE_CATALOG.map((option) => {
                const selected = filter.type === option.value
                const count = view.summary.typeCounts[option.value] ?? 0
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      setFilter((current) => ({ ...current, type: option.value }))
                    }}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                      selected
                        ? 'border-brand-500 bg-brand-50 text-brand-500'
                        : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                    )}
                  >
                    {option.label} ({count})
                  </button>
                )
              })}
            </div>

            <div className="w-48">
              <label className="sr-only" htmlFor="closure-court-filter">
                코트 필터
              </label>
              <Select
                id="closure-court-filter"
                value={filter.courtId}
                onChange={(event) => {
                  const value = event.target.value
                  setFilter((current) => ({
                    ...current,
                    courtId: value === 'ALL' ? 'ALL' : Number(value),
                  }))
                }}
              >
                <option value="ALL">전체 코트</option>
                {courts.map((court) => (
                  <option key={court.courtId} value={court.courtId}>
                    {court.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <ClosureListTable
            items={filteredItems}
            editable={view.editable}
            onDelete={(item) => {
              setModal({ type: 'delete', item })
            }}
          />
        </div>
      ) : null}

      {modal?.type === 'create' ? (
        <ClosureFormModal
          courts={courts}
          onValidated={(input) => {
            setModal({ type: 'confirm', input })
          }}
          onClose={() => {
            setModal(null)
          }}
        />
      ) : null}

      {modal?.type === 'confirm' ? (
        <ClosureCreateConfirmModal
          venueId={venueId}
          input={modal.input}
          onClose={() => {
            setModal(null)
          }}
        />
      ) : null}

      {modal?.type === 'delete' ? (
        <ClosureDeleteConfirmModal
          venueId={venueId}
          item={modal.item}
          onClose={() => {
            setModal(null)
          }}
        />
      ) : null}
    </>
  )
}
